import { LoadEmbeddedAssetsResponse } from '../shared-assets'
import {
  IapApi,
  SpendCurrencyOptions,
  SpendCurrencyResult,
  OpenStoreResult,
  SubscriptionTier,
  RunSubscriptionsResponse,
  PurchaseSubscriptionResponse,
  SubscriptionInterval,
  assertTierSupportsInterval,
  DirectPurchaseSku,
  GetCurrencyIconOptions,
} from './IapApi'
import { mockCurrencyIconBase64 } from './MockCurrencyIcon'
import { mockLog } from '../MockDebug'

const TAG = 'Mock IAP'

export class MockIapApi implements IapApi {
  private _hardCurrency: number = 100

  get hardCurrency(): number {
    return this._hardCurrency
  }

  set hardCurrency(value: number) {
    this._hardCurrency = value
  }

  async spendCurrency(
    productId: string,
    cost: number,
    options?: SpendCurrencyOptions,
  ): Promise<SpendCurrencyResult> {
    mockLog(TAG, `spendCurrency productId=${productId} cost=${cost} balance=${this._hardCurrency}`)
    // The production host bridge (H5IapWalletHandler) gates these and the call
    // RESOLVES to a result — it does not throw. Match that surface exactly.
    if (typeof productId !== 'string' || productId.length === 0) {
      return { success: false }
    }
    if (!Number.isFinite(cost) || cost <= 0) {
      return { success: false, error: 'INVALID_AMOUNT' }
    }
    const remainingHardCurrency = this._hardCurrency - cost
    if (remainingHardCurrency < 0) {
      return { success: false, error: `Not enough hard currency. Expected ${cost}, found ${this._hardCurrency}` }
    }
    this._hardCurrency = remainingHardCurrency
    return { success: true }
  }

  async getHardCurrencyBalance(): Promise<number> {
    mockLog(TAG, `getHardCurrencyBalance → ${this._hardCurrency}`)
    return this._hardCurrency
  }

  async openStore(): Promise<OpenStoreResult> {
    mockLog(TAG, 'openStore (adding 100 currency)')
    this._hardCurrency += 100
    return {
      purchased: true,
      newBalance: this._hardCurrency,
    }
  }

  /**
   * One embedded blob stands in for all eight variant/tier assets — inlining
   * the real set would bloat the SDK bundle. Options are logged, not honoured,
   * so mock output never matches a `mono` request visually.
   */
  async getCurrencyIcon(options?: GetCurrencyIconOptions): Promise<LoadEmbeddedAssetsResponse> {
    mockLog(TAG, `getCurrencyIcon (variant=${options?.variant ?? 'gradient'}, size=${options?.size ?? 'md'})`)
    return {
      base64Data: mockCurrencyIconBase64,
    }
  }

  async getSubscriptions(tier?: SubscriptionTier): Promise<RunSubscriptionsResponse> {
    return {
      'CORE': [
        {
          description: 'core subscription',
          currencyCode: 'USD',
          price: 2.99,
          interval: 'weekly'
        }
      ]
    }
  }

  async purchaseSubscription(tier: SubscriptionTier, interval: SubscriptionInterval): Promise<PurchaseSubscriptionResponse> {
    assertTierSupportsInterval(tier, interval)
    mockLog(TAG, `purchaseSubscription tier=${tier} interval=${interval}`)
    return { success: true }
  }

  async isUserSubscribed(tier: SubscriptionTier): Promise<boolean> {
    mockLog(TAG, `isUserSubscribed tier=${tier} → true`)
    return true
  }

  async hasUserMadePurchase(): Promise<boolean> {
    mockLog(TAG, 'hasUserMadePurchase → false')
    return false
  }

  async listDirectPurchaseSkus(gameId: string): Promise<DirectPurchaseSku[]> {
    mockLog(TAG, `listDirectPurchaseSkus gameId=${gameId} → []`)
    return []
  }
}
